import {
  Title,
  useListContext,
  TextInput,
  TopToolbar,
  SelectInput,
  useUpdate,
  BooleanInput,
  DateInput,
  InfiniteList,
} from "react-admin";
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Select,
  MenuItem,
  Chip,
} from "@mui/material";
import ProviderNameLinkField from "../../components/provider-name-link-field";
import {
  dateToTimestamp,
  providerDashboardBackgroundList,
  providerDashboardVettingStatusList,
  referenceStatusList,
} from "../../utils";

const filters = [
  <TextInput
    source="query"
    label="Provider Name"
    variant="outlined"
    sx={{ width: "200px", height: "32px", margin: "8px" }}
    alwaysOn
  />,
  <DateInput
    source="subscribedDateFrom"
    variant="outlined"
    sx={{ width: "200px", height: "32px", margin: "8px" }}
    label="Subscribe Date From..."
    parse={dateToTimestamp}
    alwaysOn
  />,
  <SelectInput
    source="background"
    label="Background"
    variant="outlined"
    sx={{ width: "200px", height: "32px", margin: "8px" }}
    choices={providerDashboardBackgroundList}
    alwaysOn
  />,
  <SelectInput
    source="vettingStatus"
    label="Vetting Status"
    variant="outlined"
    sx={{ width: "200px", height: "32px", margin: "8px" }}
    choices={providerDashboardVettingStatusList}
    alwaysOn
  />,
  <BooleanInput
    source="isActive"
    label="Active"
    alwaysOn
    sx={{ marginBottom: "10px", marginLeft: "4px" }}
  />,
  <BooleanInput
    source="isSubscriber"
    label="Subscriber"
    alwaysOn
    sx={{ marginBottom: "10px", marginLeft: "4px" }}
  />,
];

const OnboardingTable = () => {
  const { data, refetch, isLoading } = useListContext();
  const [update] = useUpdate();

  const handleBackgroundChange = (event, id) => {
    update(
      "Provider",
      {
        id,
        data: {
          background: event.target.value,
        },
      },
      {
        onSuccess: () => {
          // refetch in 1.2 sec to update the data
          setTimeout(refetch, 1200);
        },
      },
    );
  };

  const handleVettingStatusChange = (event, id) => {
    update(
      "Provider",
      {
        id,
        data: {
          vettingStatus: event.target.value,
        },
      },
      {
        onSuccess: () => {
          // refetch in 1.2 sec to update the data
          setTimeout(refetch, 1200);
        },
      },
    );
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <Table sx={{ margin: "8px" }}>
      <TableHead>
        <TableRow>
          <TableCell align="left" sx={{ minWidth: "240px" }}>
            Name
          </TableCell>
          <TableCell align="left" sx={{ minWidth: "160px" }}>
            Subscribe Date
          </TableCell>
          <TableCell align="left" sx={{ minWidth: "320px" }}>
            References
          </TableCell>
          <TableCell align="left" sx={{ minWidth: "160px" }}>
            Background
          </TableCell>
          <TableCell align="left" sx={{ minWidth: "160px" }}>
            Vetting Status
          </TableCell>
          <TableCell align="left" sx={{ maxWidth: "auto" }}>
            Admin Notes
          </TableCell>
          <TableCell align="left" sx={{ minWidth: "160px" }}>
            Activation Date
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {data?.map((provider, index) => (
          <TableRow key={`provider-${index}`} sx={{ verticalAlign: "top" }}>
            <TableCell>
              <ProviderNameLinkField
                firstName={provider.firstName}
                lastName={provider.lastName}
                id={provider.id}
              />
            </TableCell>
            <TableCell>
              <span>
                {provider?.["subscribedDate"]
                  ? new Date(+provider["subscribedDate"]).toDateString()
                  : provider?.["subscriptionStartDate"]
                    ? new Date(
                        +provider["subscriptionStartDate"],
                      ).toDateString()
                    : "-"}
              </span>
            </TableCell>
            <TableCell>
              {provider.references?.map((reference, refIndex) => (
                <Card
                  key={`reference-${refIndex}`}
                  style={{ margin: "8px", padding: "8px", borderRadius: 0 }}
                >
                  <span>{`${reference.name}`}</span>
                  <span>{" - "}</span>
                  <a
                    href={`tel:${reference.phoneNumber}`}
                  >{`${reference.phoneNumber}`}</a>
                  <span>{" - "}</span>
                  <span>
                    {reference.status
                      ? referenceStatusList.find(
                          (status) => status.id === reference.status,
                        )?.name
                      : ""}
                  </span>
                </Card>
              ))}
            </TableCell>
            <TableCell>
              <Select
                variant="outlined"
                style={{ width: "150px", height: "32px" }}
                onChange={(event) => handleBackgroundChange(event, provider.id)}
                value={provider.background}
              >
                {providerDashboardBackgroundList?.map((background) => (
                  <MenuItem
                    key={`background-${background.id}`}
                    value={background.id}
                  >
                    {background.name}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
            <TableCell>
              <Select
                variant="outlined"
                style={{ width: "150px", height: "32px" }}
                onChange={(event) =>
                  handleVettingStatusChange(event, provider.id)
                }
                value={provider.vettingStatus || "inProgress"}
              >
                {providerDashboardVettingStatusList?.map((vettingStatus) => (
                  <MenuItem
                    key={`vettingStatus-${vettingStatus.id}`}
                    value={vettingStatus.id}
                  >
                    {vettingStatus.name}
                  </MenuItem>
                ))}
              </Select>
            </TableCell>
            <TableCell>
              <span>{`${provider.adminNotes || ""}`}</span>
            </TableCell>
            <TableCell>
              <span>
                {provider?.isActive && provider?.["activationDate"] ? (
                  new Date(+provider["activationDate"]).toDateString()
                ) : !provider?.isActive ? (
                  <Chip label="Inactive" color="default" />
                ) : (
                  "-"
                )}
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const Actions = () => <TopToolbar></TopToolbar>;

export const OnboardingPage = () => {
  return (
    <Card>
      <Title title="Onboarding" />
      <InfiniteList
        storeKey="provider-onboarding-list"
        resource="Provider"
        perPage={100}
        filters={filters}
        filterDefaultValues={{
          query: null,
          isActive: false,
          isSubscriber: true,
          vettingStatus: "inProgress",
          subscribedDateFrom: +new Date("09/18/25"),
        }}
        sort={{ field: "subscribedDate", order: "DESC" }}
        exporter={null}
        actions={<Actions />}
        disableSyncWithLocation={true}
      >
        <OnboardingTable />
      </InfiniteList>
    </Card>
  );
};
